<template>
  <div class="custom-select" tabindex="0">
    <ul
      class="selectBox"
      :class="{ on: isDropdownOpen, disabled: props.disabled }"
      @click.stop="toggleDropdown"
    >
      <li class="selected-option">
        <div class="select-option-name">
          {{
            selectOptionName ||
            props.disableText ||
            props.optionList[0]?.name ||
            props.optionList[0]?.label
          }}
        </div>

        <transition name="fade">
          <ul v-if="isDropdownOpen" class="options">
            <li
              v-for="domain in props.optionList"
              :key="domain?.label || domain?.name"
              @click.stop="selectDomain(domain)"
            >
              <div>{{ domain?.label || domain?.name }}</div>
            </li>
          </ul>
        </transition>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
interface IOption {
  label?: string;
  name?: string;
  value: any;
}
interface IProps {
  modelValue: string | number;
  optionList: IOption[];
  disabled?: boolean;
  disableText?: string;
}
const props = defineProps<IProps>();
const dropdownId = Symbol('dropdownId');
const isDropdownOpen = ref<boolean>(false);

const emits = defineEmits(['update:modelValue', 'change']);

const toggleDropdown = () => {
  if (props.disabled) return;
  if (!isDropdownOpen.value) {
    window.dispatchEvent(
      new CustomEvent('close-all-dropdowns', { detail: dropdownId })
    );
  }
  isDropdownOpen.value = !isDropdownOpen.value;
};
const close = () => {
  isDropdownOpen.value = false;
};
const selectOptionName = computed(() => {
  const option = props.optionList.find((f) => f.value === props.modelValue);
  return option?.label || option?.name || '';
});

const selectDomain = (domain: IOption) => {
  if (props.disabled) return;
  isDropdownOpen.value = false;
  emits('update:modelValue', domain.value);
  emits('change', domain.value);
};
// 전역 이벤트로 다른 드롭다운 닫기 처리
const handleCloseAllDropdowns = (event: CustomEvent) => {
  const customEvent = event as CustomEvent<symbol>;
  if (customEvent.detail !== dropdownId) {
    close();
  }
};

onMounted(() => {
  window.addEventListener('click', close); // 외부 클릭 시 닫기
  window.addEventListener(
    'close-all-dropdowns',
    handleCloseAllDropdowns as EventListener
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('click', close);
  window.removeEventListener(
    'close-all-dropdowns',
    handleCloseAllDropdowns as EventListener
  );
});
defineExpose({ close });
</script>

<style scoped lang="scss">
select {
  appearance: none;
}
select:focus {
  outline: none;
}

.selectBox {
  color: #aaaaaa;
  text-align: left;
  position: relative;
  transition: all 0.3s;
  margin-left: 0 !important;
  width: 100%;
  height: 52px;
  display: block !important;
  cursor: pointer;
  outline: none;
  border-radius: 25px;
  border: 1px solid #d8d8d8;
  font-family: 'Noto Sans KR', sans-serif;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.selectBox::after {
  content: '';
  width: 16px;
  height: 16px;
  background: url('@assets/img/contents/icon_select-arrow.svg') no-repeat center
    right 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 15px;
  transition: transform 0.3s ease;
  margin: auto;
}

.selectBox .selected-option {
  position: unset;
  padding: 0 16px;
  cursor: pointer;
  font-size: clamp(0.875rem, 0.85rem + 0.125vw, 1rem);
  transition: 0.5s ease all;
  width: 100%;
  height: 100%;
}
.selectBox.on::after {
  transform: rotate(180deg);
}
.selectBox .select-option-name {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
}
.selectBox.open .selected-option {
  border: 1px solid #707070;
  border-bottom: 0;
  background-color: #fff;
}

.selectBox .selected-option .options {
  position: absolute;
  top: 55px;
  left: 0;
  width: calc(100% - 1px);
  margin-left: 0 !important;
  display: block;
  z-index: 10;
  max-height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #d8d8d8;
  border-radius: 14px;
  background: #fff;
}

.selectBox .selected-option .options li {
  height: 44px;
  background: #fff;
  width: calc(100% + 1px);
}

/*.selectBox .selected-option .options li:first-child,
.selectBox .selected-option .options li:first-child div {
  border-radius: 14px 14px 0 0;
  overflow: hidden;
}*/

/*.selectBox .selected-option .options li:last-child,
.selectBox .selected-option .options li:last-child div {
  border-radius: 0 0 14px 14px;
  overflow: hidden;
}*/

.selectBox .selected-option .options li div {
  display: block;
  padding: 10px 0;
  height: 100%;
  color: #363636;
  text-align: center;
  position: relative;
  width: 100%;
  background: #fff;
}

.selectBox .selected-option .options li:not(:last-child) div::after {
  content: '';
  width: 85%;
  height: 1px;
  border-bottom: 1px dashed #aaaaaa;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.selectBox .selected-option .options li div:hover {
  background: #cccc;
}

.selectBox .selected-option .options::-webkit-scrollbar {
  width: 3px;
}

.selectBox .selected-option .options::-webkit-scrollbar-thumb {
  height: 3%;
  background: #c1c1c1;
  border-radius: 8px;
}

.selectBox .selected-option .options::-webkit-scrollbar-track {
  background: transparent;
}

.selectBox.open .selected-option::before {
  content: '';
  width: 0;
  height: 0;
  border-left: 0.3em solid transparent;
  border-right: 0.3em solid transparent;
  border-top: 0.5em solid #000;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
}

/* Fade animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.voucher-wrap .section02 .select-num .custom-select .selectBox {
  background: #fff;
}

/*모바일*/
@media (min-width: 320px) and (max-width: 1279px) {
  .selectBox {
    height: 45px;
  }

  /*  .selectBox .select-option-name {
    justify-content: unset;
  }*/
}
</style>
